import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import authService from '../services/authService';
import axiosInstance from '../services/axiosInstance';
import invitationService from '../services/invitationService';
import languageService from '../services/languageService';
import { AuthContext } from './AuthContext';
import { useTranslation } from 'react-i18next';
import '../styles/RegisterForm.css';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { FaGlobe, FaEye, FaEyeSlash } from 'react-icons/fa';
import { CiMail, CiUser, CiLock } from 'react-icons/ci';
import { toast } from 'sonner';
import { trackEvent } from '../utils/analytics';
import SEO from './SEO';
import InviteSuccess from './InviteSuccess'; // <-- IMPORT

const RegisterForm = () => {
	const navigate = useNavigate();
	const { login } = useContext(AuthContext);
	const [username, setUsername] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	// registrationSuccess už nepotřebujeme pro zobrazení zprávy, protože děláme auto-login
	// ale nechám ho v kódu, kdybychom ho chtěli v budoucnu použít, nebo ho můžeme ignorovat.
	const [registrationSuccess, setRegistrationSuccess] = useState(false); 
	
	const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	
	// NOVÝ STAV PRO ANIMACI
	const [inviteSuccess, setInviteSuccess] = useState(false);

	const { t, i18n } = useTranslation();

	const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

	const languages = {
		'en': { name: 'English', flag: '/flags/en.png' },
		'cs': { name: 'Čeština', flag: '/flags/cs.png' }
	};

	if (!googleClientId) {
		console.error('Google Client ID not configured');
	}

	const getErrorMessage = (error) => {
		if (!error.response) {
			if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
				return t('register.errors.serverDown', 'Server is currently unavailable. Please try again later.');
			}
			if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
				return t('register.errors.timeout', 'Connection timeout. Please check your internet connection and try again.');
			}
			return t('register.errors.networkError', 'Unable to connect to server. Please check your internet connection.');
		}

		const status = error.response.status;
		const data = error.response.data;

		switch (status) {
			case 409:
				return t('register.errors.userExists', 'User with this email already exists.');
			case 403:
				if (typeof data === 'string' && data.includes('Maximum number of users')) {
					return t('register.errors.userLimitReached', data);
				}
				return t('register.errors.forbidden', 'Registration is currently not allowed.');
			case 400:
				if (data && typeof data === 'string') return data;
				if (data && data.message) return data.message;
				return t('register.errors.invalidData', 'Please check your input data.');
			case 422:
				return t('register.errors.validationError', 'Please check your input data and try again.');
			case 429:
				return t('register.errors.tooManyAttempts', 'Too many registration attempts. Please wait before trying again.');
			case 500:
				return t('register.errors.serverError', 'Server error occurred. Please try again later.');
			case 502:
			case 503:
			case 504:
				return t('register.errors.serverUnavailable', 'Server is temporarily unavailable. Please try again later.');
			default:
				if (typeof data === 'string') return data;
				if (data && data.message) return data.message;
				return t('register.errors.general', 'An error occurred during registration. Please try again.');
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);

		try {
			// 1. Registrace (původní volání)
			await authService.registerUser({ username, email, password });
			trackEvent('Registration', { method: 'email' });
			// toast.success(t('register.success', 'User registered successfully!')); // Můžeme nechat nebo schovat, když hned logujeme

			// 2. AUTOMATICKÝ LOGIN
			// Použijeme email a heslo, které uživatel právě zadal
			await authService.loginUser({ username: email, password });

			// 3. KONTROLA POZVÁNKY
			const pendingInvite = localStorage.getItem('pendingInvite');
			
			if (pendingInvite) {
				try {
					const { leagueId, inviteToken } = JSON.parse(pendingInvite);
					
					// Akceptace
					await invitationService.acceptInvite(leagueId, inviteToken);
					trackEvent('Invite Accepted', { source: 'pending_register' });
					
					localStorage.removeItem('pendingInvite');

					// Zobrazit animaci
					setInviteSuccess(true);

					// Odložený redirect a update kontextu
					setTimeout(async () => {
						await login(); // Update AuthContext
						navigate(`/league/${leagueId}`);
					}, 2000);

				} catch (inviteError) {
					console.error("Auto-accept failed", inviteError);
					// Fallback
					const { leagueId, inviteToken } = JSON.parse(pendingInvite);
					localStorage.removeItem('pendingInvite');
					await login();
					navigate(`/invite/league/${leagueId}/${inviteToken}`);
				}
			} else {
				// Standardní flow bez pozvánky
				await login();
				navigate('/dashboard');
			}

		} catch (error) {
			trackEvent('Registration Failed', { 
				method: 'email',
				error: error.response?.status || 'unknown' 
			});
			const errorMessage = getErrorMessage(error);
			toast.error(errorMessage);
			setRegistrationSuccess(false);
		} finally {
			// Loading vypneme jen pokud NEJSME v úspěšném invite stavu
			if (!inviteSuccess) {
				setLoading(false);
			}
		}
	};

	const handleGoogleLoginSuccess = async (credentialResponse) => {
		setLoading(true);

		try {
			await axiosInstance.get('/auth/csrf');
			await authService.loginWithGoogle(credentialResponse.credential);
			
			// Nevoláme hned login(), počkáme na check pozvánky

			trackEvent('Registration', { method: 'google' });
			const pendingInvite = localStorage.getItem('pendingInvite');
			
			if (pendingInvite) {
				try {
					const { leagueId, inviteToken } = JSON.parse(pendingInvite);
					await invitationService.acceptInvite(leagueId, inviteToken);
					trackEvent('Invite Accepted', { source: 'pending_register' });
					
					localStorage.removeItem('pendingInvite');

					// Zobrazit animaci
					setInviteSuccess(true);

					setTimeout(async () => {
						await login();
						navigate(`/league/${leagueId}`);
					}, 2000);

				} catch (err) {
					// Fallback
					const { leagueId, inviteToken } = JSON.parse(pendingInvite);
					localStorage.removeItem('pendingInvite');
					await login();
					navigate(`/invite/league/${leagueId}/${inviteToken}`);
				}
			} else {
				await login();
				navigate('/dashboard');
			}
		} catch (error) {
			console.error('Google login error:', error);
			trackEvent('Registration Failed', { method: 'google' });
			const errorMessage = getErrorMessage(error);
			toast.error(errorMessage);
		} finally {
			if (!inviteSuccess) {
				setLoading(false);
			}
		}
	};

	const handleGoogleLoginError = () => {
		toast.error(t('login.errors.googleError', 'Error logging in with Google. Please try again or use email login.'));
		setLoading(false);
	};

	const handleLanguageChange = (lang) => {
		languageService.changeLanguageAndPersist(i18n, lang);
		setLanguageDropdownOpen(false);
	};

	const toggleLanguageDropdown = () => {
		setLanguageDropdownOpen(!languageDropdownOpen);
	};

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	return (
		<div className="register-container">
			<SEO title={t('noAccountRegister', { defaultValue: "Přihlášení" })} description="Založ si účet na Scoreo a začni tipovat výsledky s přáteli." />
			<div className="register-language-selector">
				<button
					onClick={toggleLanguageDropdown}
					className="language-toggle-btn"
					aria-label="Change language"
				>
					<FaGlobe />
				</button>
				{languageDropdownOpen && (
					<div className="language-dropdown">
						{Object.entries(languages).map(([code, { name, flag }]) => (
							<div
								key={code}
								onClick={() => handleLanguageChange(code)}
								className="language-option"
							>
								<img
									src={flag}
									alt={`${name} flag`}
									className="language-flag"
								/>
								{name}
							</div>
						))}
					</div>
				)}
			</div>

			<div className="register-header">
				<Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
					<h2>Scoreo</h2>
				</Link>
				{/* Schováme texty, pokud běží animace úspěchu, aby to bylo čisté */}
				{!inviteSuccess && (
					<>
						<h3>{t('register.welcome')}</h3>
						<p>{t('register.info')}</p>
					</>
				)}
			</div>

			<div className="register-card-wrapper">
				<div className="register-card">
					{/* PODMÍNKA: Buď animace, nebo formulář. Formulář je beze změny. */}
					{inviteSuccess ? (
						<InviteSuccess 
							subtitle={t('invite.joining', 'Vytvářím účet a připojuji...')} 
						/>
					) : (
						<>
							<form onSubmit={handleSubmit}>
								<div className="input-group">
									<label htmlFor="email">Email *</label>
									<div className="input-wrapper">
										<CiMail className="input-icon" />
										<input
											type="email"
											id="email"
											placeholder="jan.novak@gmail.com"
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											required
											disabled={loading}
										/>
									</div>
								</div>

								<div className="input-group">
									<label htmlFor="username">{t('usernameSimple') || 'Username'} *</label>
									<div className="input-wrapper">
										<CiUser className="input-icon" />
										<input
											type="text"
											id="username"
											placeholder="JanNovak"
											value={username}
											onChange={(e) => setUsername(e.target.value)}
											required
											disabled={loading}
										/>
									</div>
								</div>

								<div className="input-group">
									<label htmlFor="password">{t('password') || 'Password'} *</label>
									<div className="input-wrapper">
										<CiLock className="input-icon" />
										<input
											type={showPassword ? 'text' : 'password'}
											id="password"
											placeholder="************"
											value={password}
											onChange={(e) => setPassword(e.target.value)}
											required
											disabled={loading}
										/>
										{showPassword ? (
											<FaEyeSlash className="password-toggle-icon" onClick={togglePasswordVisibility} />
										) : (
											<FaEye className="password-toggle-icon" onClick={togglePasswordVisibility} />
										)}
									</div>
								</div>

								<button type="submit" className="btn-primary" disabled={loading}>
									{loading ? t('registering', 'Registering...') : (t('noAccountRegister') || 'Register')}
								</button>
							</form>

							<div className="register-divider">
								<span>{t('login.socials', 'Or continue with')}</span>
							</div>
							
							<div className="google-button-wrapper">
								<GoogleOAuthProvider clientId={googleClientId}>
									<GoogleLogin
										onSuccess={handleGoogleLoginSuccess}
										onError={handleGoogleLoginError}
										useOneTap={false}
										theme="outline"
										text="signup_with" 
										shape="rectangular"
										size="large"
										locale="cs"
										disabled={loading}
									/>
								</GoogleOAuthProvider>
							</div>

							<p className="signup-link">
								{t('alreadyHaveAnAccount') || 'Already have an account?'} <Link to="/login">{t('signIn') || 'Sign In'}</Link>
							</p>
						</>
					)}
				</div>

				{!inviteSuccess && (
					<div className="terms-card">
						<p>
							{t('register.consent') || 'By registering, you agree to our'} <a href="/legal" target="_blank" rel="noopener noreferrer">{t('login.terms') || 'Terms of Service'}</a> {t('login.and') || 'and'} <a href="/legal" target="_blank" rel="noopener noreferrer">{t('login.privacy') || 'Privacy Policy'}</a>
						</p>
					</div>
				)}
			</div>

			{loading && !inviteSuccess && (
				<div className="register-loading">
					<div className="spinner"></div>
					<p>{t('loading')}</p>
				</div>
			)}
		</div>
	);
};

export default RegisterForm;